import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../assets/images/logo.png';
import authService from '../services/authService';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // ✅ Function to update auth state
  const updateAuthState = () => {
    const token = authService.getToken();
    const userData = authService.getCurrentUser();
    setIsLoggedIn(!!token);
    setUser(userData);
    console.log('Auth state updated:', { isLoggedIn: !!token, user: userData });
  };

  useEffect(() => {
    // Initial check
    updateAuthState();

    // Listen for storage changes (login/logout from other tabs)
    window.addEventListener('storage', updateAuthState);
    
    // ✅ Listen for custom event for same-tab updates
    window.addEventListener('authChange', updateAuthState);
    
    return () => {
      window.removeEventListener('storage', updateAuthState);
      window.removeEventListener('authChange', updateAuthState);
    };
  }, []);

  useEffect(() => {
    // Calculate header height
    const header = document.querySelector('.header');
    if (header) {
      document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
    }

    const handleResize = () => {
      const headerEl = document.querySelector('.header');
      if (headerEl) {
        document.documentElement.style.setProperty('--header-height', `${headerEl.offsetHeight}px`);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setShowUserDropdown(false);
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (!isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setShowUserDropdown(false);
    document.body.style.overflow = 'unset';
  };

  const handleLogout = () => {
    authService.logout();
    updateAuthState(); // ✅ Immediately update state
    setShowUserDropdown(false);
    closeMenu();
    navigate('/');
    // ✅ Dispatch event for other components
    window.dispatchEvent(new Event('authChange'));
  };

  const toggleUserDropdown = () => {
    setShowUserDropdown(!showUserDropdown);
  };

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <nav className="navbar">
        {/* Logo on Left */}
        <div className="logo-container">
          <Link to="/" className="logo" onClick={closeMenu}>
            <img src={logo} alt="RedGoTrip" />
          </Link>
        </div>

        {/* Mobile Menu Button on Right */}
        <button 
          className={`menu-toggle ${isMenuOpen ? 'active' : ''}`} 
          onClick={toggleMenu}
          aria-label="Toggle menu"
        >
          <span className="bar"></span>
          <span className="bar"></span>
          <span className="bar"></span>
        </button>

        {/* Desktop Navigation Menu */}
        <div className={`nav-links ${isMenuOpen ? 'active' : ''}`}>
          <Link to="/" className="nav-link" onClick={closeMenu}>
            <i className="fas fa-home"></i>
            <span>Home</span>
          </Link>
          <Link to="/packages" className="nav-link" onClick={closeMenu}>
            <i className="fas fa-box"></i>
            <span>Holidays</span>
          </Link>
          <Link to="/destinations" className="nav-link" onClick={closeMenu}>
            <i className="fas fa-map-marked-alt"></i>
            <span>Destinations</span>
          </Link>
          <Link to="/camping" className="nav-link" onClick={closeMenu}>
            <i className="fas fa-campground"></i>
            <span>Camping</span>
          </Link>
      
          <Link to="/booking/track" className="nav-link" onClick={closeMenu}>
            <i className="fas fa-search"></i>
            <span>Track Booking</span>
          </Link>

          {/* User Authentication Section */}
          {isLoggedIn ? (
            <div className="user-menu-container">
              <button 
                className="user-menu-btn"
                onClick={toggleUserDropdown}
              >
                <div className="user-avatar">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <span className="user-name">{user?.name?.split(' ')[0]}</span>
                <i className={`fas fa-chevron-${showUserDropdown ? 'up' : 'down'}`}></i>
              </button>

              {showUserDropdown && (
                <div className="user-dropdown">
                  <Link to="/profile" className="dropdown-item" onClick={closeMenu}>
                    <i className="fas fa-user"></i>
                    <span>My Profile</span>
                  </Link>
<Link to="/wallet" className="dropdown-item" onClick={closeMenu}>
    <i className="fas fa-wallet"></i>
    <span>My Wallet</span>
</Link>
                  <Link to="/my-bookings" className="dropdown-item" onClick={closeMenu}>
                    <i className="fas fa-calendar-check"></i>
                    <span>My Bookings</span>
                  </Link>
                  <Link to="/wishlist" className="dropdown-item" onClick={closeMenu}>
                    <i className="fas fa-heart"></i>
                    <span>Wishlist</span>
                  </Link>
                  <hr className="dropdown-divider" />
                  <button className="dropdown-item logout-btn" onClick={handleLogout}>
                    <i className="fas fa-sign-out-alt"></i>
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="auth-buttons">
              <Link to="/login" className="login-btn" onClick={closeMenu}>
                <i className="fas fa-sign-in-alt"></i>
                <span>Login</span>
              </Link>
              <Link to="/register" className="signup-btn" onClick={closeMenu}>
                <i className="fas fa-user-plus"></i>
                <span>Register</span>
              </Link>
            </div>
          )}

    

          {/* Mobile Footer Links */}
          <div className="mobile-footer">
            <div className="mobile-social">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-facebook-f"></i>
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-instagram"></i>
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-twitter"></i>
              </a>
            </div>
            <div className="mobile-contact">
              <a href="tel:+919335444047">
                <i className="fas fa-phone-alt"></i> +91 9335444047
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && <div className="menu-overlay" onClick={closeMenu}></div>}
    </header>
  );
};

export default Header;
